import founderJuliette from '@/assets/images/founder_02.png';
import founderShade from '@/assets/images/founder_01.png';
import heroVideo from '@/assets/videos/about_us_hero.mp4';
import {
  FinalCta,
  HEADLINE_FROM,
  HEADLINE_TO,
  HeroLights,
  useSectionReveal,
} from '@/components/marketing';
import { Glow, Icon } from '@/components/ui';
import { cn } from '@/utils/cn';

/**
 * How far the footage is scaled past `object-cover`.
 *
 * The file is a 1920x1080 export with the actual footage letterboxed inside
 * it on white -- a title above, a margin all round -- occupying the middle
 * 80% in both directions. Scaling by 1.25 about the centre puts exactly that
 * 80% across the frame; the little extra keeps a hairline of white from
 * showing at the edges when the box rounds a pixel the other way.
 */
const VIDEO_ZOOM = 1.27;

const STORY = [
  {
    title: 'Who We Are',
    paragraphs: [
      'Wonder was created by two 21-year-old women student founders, after experiencing the challenges of the internship market firsthand.',
      'Being both students and startup founders has allowed us to experience both sides of the same problem. We know how frustrating it can be for students to search, apply, wait, and struggle to discover the right opportunities. At the same time, we understand how difficult and time-consuming it can be for startups to find the right young talent.',
      'This perspective is at the heart of Wonder and everything we are building.',
    ],
  },
  {
    title: 'Why We Started',
    paragraphs: [
      'Throughout university, we saw students spending hours searching through job platforms, applying to the same recognizable companies and competing with hundreds of others for a limited number of internships. However, hiring interns is not exactly easier either.',
      "We experienced the first side of the problem ourselves: fewer opportunities, more competition, and hours spent applying to the same recognizable companies. Yet having both worked in startups, we knew there was a much larger world of meaningful, hands-on opportunities that many students simply weren't seeing.",
      'Then, as startup founders and after speaking with many others, we came to understand the other side. Smaller startups are often overshadowed by larger companies, but when their opportunities do get attention, founders can receive hundreds of applications within days. Somewhere in that pile might be an incredible candidate. The challenge is finding them without spending hours screening CVs.',
      'That’s when we realized the internship market didn’t only have a lack of visibility. **It had a matching problem.** We built Wonder because we think there has to be a better way for ambitious students and great startups to find each other.',
    ],
  },
  {
    title: 'How We Work',
    paragraphs: [
      "Because Wonder is built specifically for startup internships, every student goes through a personalized matching process. Opportunities are matched based on what they want to learn, what they're good at, what interests them, and the kind of startup they actually want to join. We want to avoid the habit of applying everywhere simply hoping to get noticed. In case of a rejection, a human response will be sent to the student, allowing for closure, rather than simple ignorance.",
      'For startups, we built LLM-powered matching and AI algorithms that understand what each role requires, evaluate applicants against those needs, and rank the strongest matches. Instead of screening hundreds of CVs, founders can focus on a shortlist of the students most relevant to them.',
    ],
  },
  {
    title: 'Our Mission & Values',
    paragraphs: [
      'At Wonder, we want to make the world of internships a better place through more choice and better matches for BOTH sides.',
      "We believe great opportunities shouldn't go unseen and great candidates shouldn't get lost in a pile of applications. Students should be able to discover companies and career paths they may never have considered, while startups should have a simpler, smarter way to find the people who can genuinely help them grow.",
      'We aim to become the go-to hiring platform for ambitious students and growing startups.',
    ],
  },
];

/** Placeholder handles -- swap for the founders' real accounts before launch. */
const SOCIALS = [
  { icon: 'x', label: 'on X', href: 'https://x.com' },
  { icon: 'linkedin', label: 'on LinkedIn', href: 'https://www.linkedin.com' },
  { icon: 'instagram', label: 'on Instagram', href: 'https://www.instagram.com' },
  { icon: 'facebook', label: 'on Facebook', href: 'https://www.facebook.com' },
];

const FOUNDERS = [
  {
    name: 'Shadé Okewole-Retat',
    role: 'CEO & Co-founder',
    image: founderShade,
    paragraphs: [
      "Hi I'm Shadé Okewole-Retat. Raised between Paris and London, I have always been driven to launch my own ventures. Attending ESCP provided a strong foundation in strategic business and entrepreneurial dynamics, while my international upbringing has shaped my perspective on growth opportunities within startups and scaling companies.",
      "Professionally, I have focused on business development, collaborating with scaling firms to support their growth and strategic evolution. Alongside co-founding Wonder, I am currently pursuing a Master's degree in Entrepreneurship, Innovation, and Management at Imperial College London.",
    ],
  },
  {
    name: 'Juliette Brasselet',
    role: 'COO & Co-founder',
    image: founderJuliette,
    paragraphs: [
      "Hi I'm Juliette Brasselet. I grew up between France and Miami, moving to the U.S. at 12 before later returning to Europe to study at ESCP. Living and studying between different countries shaped much of the way I approach new environments and opportunities.",
      "Professionally, I've explored very different industries, from luxury at Berluti and Longchamp to interior design in Miami and, eventually, startups. Those experiences gave me exposure to customer relationships, operations, marketing and business development.",
    ],
  },
];

export function AboutPage() {
  return (
    <>
      <AboutHero />
      <Intro />
      <Story />
      <Founders />
      <FinalCta />
    </>
  );
}

/**
 * "Built by students, made for startups."
 *
 * A wide, shallow band of footage with the headline set on it, low and to the
 * left. The footage sits behind a purple wash and a fade to the page's own
 * ground at the foot, so the copy holds its contrast on any frame and the band
 * ends as a surface rather than a hard edge.
 */
function AboutHero() {
  const root = useSectionReveal(
    ({ reveal }) => {
      reveal(
        '.js-about-backdrop',
        { scale: 1.05, opacity: 0 },
        { duration: 1.8, ease: 'power2.out' },
        0,
      );
      reveal('.js-about-line', HEADLINE_FROM, HEADLINE_TO, 0.2);
    },
    { start: 'top 80%', once: true },
  );

  return (
    <section
      ref={root}
      // The frame's band: 1920 x 863, so 44.95vw tall on desktop. Taller on
      // small screens, where that ratio would leave no room for the copy.
      className="relative isolate flex min-h-[70vh] flex-col justify-center overflow-hidden pt-28 pb-16 lg:min-h-0 lg:h-[44.95vw] lg:pt-32 lg:pb-20"
    >
      <div className="js-about-backdrop bg-night-raised absolute inset-0 -z-10" aria-hidden="true">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          style={{ transform: `scale(${VIDEO_ZOOM})` }}
          src={heroVideo}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="bg-iris-700/40 absolute inset-0 mix-blend-multiply" />
        {/* The frame's overlays, three edges of the page's own #28003C in one
            layer: heavy at the foot, where the band meets the section below;
            a lighter fall from the top, under the header; and a wedge in from
            the left, which is the ground the headline sits on. The centre and
            the right, where the speaker stands, are left to the wash alone. */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: [
              'linear-gradient(0deg, #28003C 0%, rgba(40, 0, 60, 0.6) 30%, rgba(40, 0, 60, 0) 70%)',
              'linear-gradient(180deg, rgba(40, 0, 60, 0.7) 0%, rgba(40, 0, 60, 0) 38%)',
              'linear-gradient(90deg, rgba(40, 0, 60, 0.8) 0%, rgba(40, 0, 60, 0.35) 30%, rgba(40, 0, 60, 0) 58%)',
            ].join(', '),
          }}
        />
      </div>

      {/* A plain wrapper between the flex section and the measure: the
          measure is `mx-auto` with `width: auto`, and as a flex item that
          shrinks to its content and floats to the middle of the screen. */}
      <div>
        <div className="wonder-container wonder-measure relative">
          {/* Inspector: Inter Tight 600, 60 on 77.52, -1.52px, uppercase, set
              at the vertical centre of the band. */}
          <h1 className="font-tight text-left text-[clamp(1.75rem,3.125vw,3.75rem)] leading-[1.292] font-semibold tracking-[-0.0253em] uppercase">
            <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
              <span className="js-about-line text-on-night block">Built by students, made</span>
            </span>{' '}
            <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
              <span className="js-about-line text-iris-300 block">for startups</span>
            </span>
          </h1>
        </div>
      </div>
    </section>
  );
}

function Intro() {
  const root = useSectionReveal(({ reveal }) => {
    reveal(
      '.js-intro',
      { y: 24, opacity: 0 },
      { duration: 0.9, stagger: 0.15, ease: 'power3.out' },
    );
  });

  return (
    <section ref={root} className="bg-night relative overflow-x-clip py-24 lg:py-36">
      <HeroLights />
      <div className="wonder-container relative mx-auto max-w-3xl text-center">
        {/* Inspector: Inter 400, 24 on 18, +2px, uppercase, #C99FFF; then
            Inter Tight Medium Italic, 36 on 48, white. */}
        <p className="js-intro text-[clamp(1.125rem,1.25vw,1.5rem)] leading-none font-normal tracking-[0.083em] text-[#C99FFF] uppercase">
          About us
        </p>
        <p className="js-intro text-on-night font-tight mt-8 text-[clamp(1.375rem,1.875vw,2.25rem)] leading-[1.3333] font-medium italic">
          A world of startup opportunities matched to your interests, ambitions, and potential. The
          right talent brought closer to every startup.
        </p>
      </div>
    </section>
  );
}

/**
 * The four chapters. Each is its own reveal, so a reader working down the page
 * meets every heading rising as it arrives rather than waiting on a single
 * clock started at the top: the title climbs out of its mask, then the
 * paragraphs settle in one after another. Plain text, not typed -- four
 * paragraphs of typewriter is a wall the reader stares at, and the founders'
 * short bios are where that effect earns its keep.
 */
function Story() {
  return (
    // No ground of its own -- the page's night shows through -- so the intro's
    // spot lights can fall on down into it instead of stopping at a hard edge.
    <div className="relative overflow-hidden pb-16 lg:pb-24">
      <Glow className="top-1/3 -right-48" size={720} tone="lilac" opacity={0.08} />
      {STORY.map((chapter) => (
        <Chapter key={chapter.title} {...chapter} />
      ))}
    </div>
  );
}

/** `**like this**` in copy marks a run to set in bold. */
const BOLD = /(\*\*[^*]+\*\*)/;

function Rich({ text }) {
  return text.split(BOLD).map((segment, index) =>
    segment.startsWith('**') ? (
      <strong key={index} className="text-on-night font-semibold">
        {segment.slice(2, -2)}
      </strong>
    ) : (
      <span key={index}>{segment}</span>
    ),
  );
}

/**
 * Typewriter copy.
 *
 * Every character is its own span, hidden until the section's timeline
 * steps through them in order -- `js-type-char` is what the timeline
 * selects. The
 * full text is repeated once for assistive tech, since a screen reader would
 * otherwise announce six hundred one-letter spans.
 */
function Typed({ text, className }) {
  return (
    <p className={className}>
      <span className="sr-only">{text.replaceAll('**', '')}</span>
      <span aria-hidden="true">
        {text.split(BOLD).map((segment, index) => {
          const bold = segment.startsWith('**');
          const chars = [...(bold ? segment.slice(2, -2) : segment)].map((char, at) => (
            <span key={at} className="js-type-char">
              {char}
            </span>
          ));
          return bold ? (
            <strong key={index} className="text-on-night font-semibold">
              {chars}
            </strong>
          ) : (
            <span key={index}>{chars}</span>
          );
        })}
      </span>
    </p>
  );
}

/** Seconds per character. A 600-character paragraph types in about 3s. */
const TYPE_SPEED = 0.005;

/** A single story chapter: a heading and its paragraphs, revealed on arrival. */
function Chapter({ title, paragraphs }) {
  const root = useSectionReveal(
    ({ reveal }) => {
      reveal('.js-chapter-title', HEADLINE_FROM, { ...HEADLINE_TO, duration: 1.6 });
      reveal(
        '.js-chapter-copy',
        { y: 24, opacity: 0 },
        { duration: 0.9, stagger: 0.15, ease: 'power3.out' },
        0.35,
      );
    },
    { start: 'top 80%', once: true },
  );

  return (
    <section ref={root} className="wonder-container wonder-measure relative py-10 lg:py-14">
      {/* Inspector: Inter Tight 500, 50 on 66, -2px; body Inter Tight 400,
          20 on 30, white at 80%. */}
      <h2 className="font-tight text-center text-[clamp(2rem,2.6vw,3.125rem)] leading-[1.32] font-medium tracking-[-0.04em]">
        <span className="block overflow-hidden pb-[0.1em] mb-[-0.1em]">
          <span className="js-chapter-title text-on-night block">{title}</span>
        </span>
      </h2>
      <div className="mt-8 space-y-5">
        {paragraphs.map((paragraph) => (
          <p
            key={paragraph}
            className="js-chapter-copy font-tight text-[clamp(1rem,1.05vw,1.25rem)] leading-normal text-white/80"
          >
            <Rich text={paragraph} />
          </p>
        ))}
      </div>
    </section>
  );
}

function Founders() {
  return (
    <div className="bg-night relative overflow-hidden py-8 lg:py-12">
      {FOUNDERS.map((founder, index) => (
        <Founder key={founder.name} {...founder} flip={index % 2 === 1} />
      ))}
    </div>
  );
}

/**
 * One founder: copy on one side, portrait on the other, and the sides swap
 * on every second row so the two read as a conversation down the page rather
 * than a list.
 */
function Founder({ name, role, image, paragraphs, flip }) {
  const root = useSectionReveal(({ reveal, select }) => {
    reveal('.js-founder-name', HEADLINE_FROM, { ...HEADLINE_TO, duration: 1.4 });
    reveal(
      '.js-founder-copy',
      { y: 20, opacity: 0 },
      { duration: 0.8, stagger: 0.1, ease: 'power3.out' },
      0.25,
    );
    reveal(
      select('.js-type-char'),
      { opacity: 0 },
      { duration: 0.01, stagger: TYPE_SPEED, ease: 'none' },
      0.5,
    );
    reveal(
      '.js-founder-portrait',
      { x: flip ? -48 : 48, opacity: 0, scale: 0.96 },
      { duration: 1.2, ease: 'power3.out' },
      0.1,
    );
  });

  return (
    <section
      ref={root}
      // Measured off the frame: a 512px portrait, the copy taking whatever is
      // left, and roughly 85px between them. Equal halves put the copy in a
      // 700px column and the portrait hard against the far edge.
      className={cn(
        'wonder-container wonder-measure relative grid items-center gap-10 py-16 lg:gap-x-21 lg:py-24',
        flip ? 'lg:grid-cols-[32rem_minmax(0,1fr)]' : 'lg:grid-cols-[minmax(0,1fr)_32rem]',
      )}
    >
      <div className={cn(flip && 'lg:order-2')}>
        <h2 className="text-[clamp(1.75rem,2.4vw,2.5rem)] leading-tight font-semibold tracking-[-0.02em]">
          <span className="block overflow-hidden pb-[0.1em] mb-[-0.1em]">
            <span className="js-founder-name text-on-night block">{name}</span>
          </span>
        </h2>
        <p className="js-founder-copy text-iris-300 mt-3 text-sm font-medium tracking-[0.22em] uppercase">
          {role}
        </p>
        <div className="mt-6 space-y-4">
          {paragraphs.map((paragraph, index) => (
            <Typed
              key={index}
              text={paragraph}
              className="text-on-night-muted text-[0.9375rem] leading-relaxed"
            />
          ))}
        </div>
        <ul className="js-founder-copy mt-6 flex gap-3">
          {SOCIALS.map((social) => (
            <li key={social.icon}>
              <a
                href={social.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`${name} ${social.label}`}
                className={cn(
                  'bg-iris-500/35 text-on-night hover:text-night grid size-9 place-items-center rounded-full',
                  // Fill on hover: a lilac disc grows from the centre. The
                  // gradient is the disc, background-size is what animates.
                  'bg-[radial-gradient(circle,var(--color-iris-300)_70.7%,transparent_71%)] bg-center bg-no-repeat',
                  'bg-size-[0%_0%] hover:bg-size-[100%_100%]',
                  'transition-[background-size,color] duration-300 ease-out',
                )}
              >
                <Icon name={social.icon} size={16} />
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div
        className={cn(
          'js-founder-portrait group bg-night-raised aspect-square w-full max-w-lg overflow-hidden rounded-3xl border border-[#EFE6F724]',
          flip && 'lg:order-1',
        )}
      >
        {image && (
          <img
            src={image}
            alt={name}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.08]"
          />
        )}
      </div>
    </section>
  );
}
